import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  deriveTargetHistory,
  formatWeightCompact,
  muscleKey,
  type CatalogMuscleSlug,
  type TargetChange,
  type TargetHistoryEntry,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { METRIC } from "../ProgramComposer/useWeeklyTargetsEditor";

// The server-authored audit trail for a set of weekly targets — "who moved
// this, when, from what to what". Extracted from the targets editor (ME-1) so
// one list serves both homes: the coach editing a program's targets and the
// athlete editing their own. The entries are the same shape from either route.
//
// Authorship is NOT decided here: the host knows who "you" are on its surface
// (on the coach's screen the athlete's edits carry the athlete's name; on Me
// they read "You"), so it passes `resolveAuthor`. Baking that rule in would
// give one of the two surfaces the wrong labels.

export type TargetHistoryListProps = {
  theme: UiTheme;
  /** null = not loaded yet. An empty array is a LOADED, empty trail. */
  history: TargetHistoryEntry[] | null;
  historyTruncated: boolean;
  loadFailed: boolean;
  /** Author label for an entry's `bySub`. */
  resolveAuthor: (bySub: string) => string;
  /** Slug → display name, resolved against the live catalog by the host. */
  resolveExerciseName: (slug: string, fallback?: string) => string;
  units: UnitsPreference;
  locale: string;
};

export function TargetHistoryList({
  theme,
  history,
  historyTruncated,
  loadFailed,
  resolveAuthor,
  resolveExerciseName,
  units,
  locale,
}: TargetHistoryListProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const historyRows = useMemo(
    () => deriveTargetHistory(history ?? []),
    [history],
  );

  const dtf = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [locale],
  );

  // Metric-aware value formatting for the audit trail (tonnage in the reader's
  // unit; counts bare). One notion of "how a target value reads".
  const fmtValue = (metric: TargetChange["metric"], n: number): string =>
    metric === "tonnage" ? formatWeightCompact(n, units, locale) : String(n);

  const changeLabel = (ch: TargetChange): string =>
    ch.axis === "exercise"
      ? resolveExerciseName(ch.key)
      : ch.axis === "muscle"
        ? t(muscleKey(ch.key as CatalogMuscleSlug))
        : ch.key;

  const changeText = (ch: TargetChange): string => {
    const metricLabel = t(METRIC[ch.metric].chip);
    const body =
      ch.kind === "set"
        ? t("coach.targets.histSet", {
            value: fmtValue(ch.metric, ch.to ?? 0),
            defaultValue: "set to {{value}}",
          })
        : ch.kind === "removed"
          ? t("coach.targets.histRemoved", { defaultValue: "removed" })
          : t("coach.targets.histChanged", {
              from: fmtValue(ch.metric, ch.from ?? 0),
              to: fmtValue(ch.metric, ch.to ?? 0),
              defaultValue: "{{from}} → {{to}}",
            });
    // The whole line goes through one key so word order + punctuation (e.g. the
    // French space-before-colon) are the translator's, not JS's.
    return t("coach.targets.histLine", {
      label: changeLabel(ch),
      metric: metricLabel,
      body,
      defaultValue: "{{label}} · {{metric}}: {{body}}",
    });
  };

  // Three states kept distinct, never collapsed (empty-vs-unresolved): a failed
  // read says so, a not-yet-loaded read says "loading" (history is `null` until
  // it lands, so `?? []` would fake "no changes"), and only a LOADED empty trail
  // reads "No changes yet."
  if (loadFailed)
    return (
      <Text style={styles.historyEmpty}>
        {t("coach.targets.errLoad", {
          defaultValue: "Couldn't load these targets. Go back and retry.",
        })}
      </Text>
    );
  if (history === null)
    return (
      <Text style={styles.historyEmpty}>
        {t("coach.targets.historyLoading", { defaultValue: "Loading…" })}
      </Text>
    );
  if (historyRows.length === 0)
    return (
      <Text style={styles.historyEmpty}>
        {t("coach.targets.historyEmpty", { defaultValue: "No changes yet." })}
      </Text>
    );

  return (
    <>
      {historyRows.map((row) => {
        const author = resolveAuthor(row.bySub);
        const when = dtf.format(new Date(row.at));
        const lines =
          row.changes.length === 0
            ? [t("coach.targets.histGeneric", { defaultValue: "Targets updated" })]
            : row.changes.map(changeText);
        return (
          // One a11y announcement per change: author, when, then each line.
          <View
            key={row.id}
            style={styles.histRow}
            accessible
            accessibilityLabel={[author, when, ...lines].join(". ")}
          >
            <View style={styles.histHead}>
              <Text style={styles.histAuthor}>{author}</Text>
              <Text style={styles.histWhen}>{when}</Text>
            </View>
            {lines.map((line, i) => (
              <Text key={i} style={styles.histChange}>
                {line}
              </Text>
            ))}
          </View>
        );
      })}
      {historyTruncated ? (
        <Text style={styles.historyEmpty}>
          {t("coach.targets.historyTruncated", {
            defaultValue: "Showing the most recent 1000 changes.",
          })}
        </Text>
      ) : null}
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    historyEmpty: { color: p.ink3, fontSize: ty.sizes.sm },
    histRow: {
      gap: 2,
      paddingVertical: s.s2,
      borderTopWidth: 1,
      borderTopColor: p.border,
    },
    histHead: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: s.s3,
    },
    histAuthor: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    histWhen: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontVariant: ["tabular-nums"],
    },
    histChange: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      lineHeight: ty.sizes.sm * 1.4,
    },
  });
}
